import "./_dropdown-field.scss";
import React, {useEffect, useState} from 'react';
import {Dropdown, DropdownItem, RedAsterisk} from "../../../../components";
import {isValid, sanitize} from "../../../validation/ValidationUtils";
import {saveSelector} from "../../../slices/NavigationSlice";
import {useAppSelector} from "../../../../store/hooks";
import {kebabCase} from "../../../../utils/stringUtils";

export type DropdownItem = {
    value: string;
    itemText: string;
};

interface DropdownFieldProps {
    value: string;
    label: string;
    items: DropdownItem[];
    onChange: (item: string) => void;
    required?: boolean;
    dataTestId?: string;
}

function DropdownField({value, label, items, onChange, required, dataTestId = kebabCase(label) + "-dropdown"}: Readonly<DropdownFieldProps>) {
    const DROPDOWN_HEIGHT_MAX = 220;

    const [dropdownItems, setDropdownItems] = useState(items);
    const [selectedValue, setSelectedValue] = useState<string>(value);
    const [hasError, setHasError] = useState<boolean>(false);
    const saveHasBeenClicked = useAppSelector(saveSelector);
    const [dropdownHeight, setDropdownHeight] = useState<number>(220);

    useEffect(() => {
        setDropdownItems(items);
        const height = items.length * 45;
        setDropdownHeight(height > DROPDOWN_HEIGHT_MAX ? DROPDOWN_HEIGHT_MAX : height);
    }, [items]);

    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    useEffect(() => {
        if (dropdownItems.length > 0) {
            setSelectedValue(value);
        }
    }, [dropdownItems]);

    useEffect(() => {
        onChange(selectedValue);
    }, [selectedValue]);

    useEffect(() => {
        setHasError((required ?? false) && saveHasBeenClicked && !isValid(value));
    }, [required, saveHasBeenClicked, value]);

    if (dropdownItems.length === 0) {
        return null;
    }

    const panelHeight = (dropdownHeight >= DROPDOWN_HEIGHT_MAX && items.length > 6) ? dropdownHeight : "auto";

    return (
        <div className={`layout-data-entry-form__field ${kebabCase(label)}-dropdown-field`} data-testid={dataTestId}>
            <label id={kebabCase(label) + "-dropdown-label"} className="h5">
                {label}
                {required && <RedAsterisk />}
            </label>
            <Dropdown
                className={`${kebabCase(label)}-dropdown-box`}
                name={kebabCase(label) + "-dropdown"}
                id={kebabCase(label) + "-dropdown"}
                aria-label={kebabCase(label) + "-dropdown"}
                aria-labelledby={kebabCase(label) + "-dropdown-label"}
                alignRight={false}
                buttonIcon="right"
                buttonKind="secondary"
                defaultPageSize={15}
                dropdownKind="select"
                dynamicDropAlign={true}
                dynamicDropDirection={true}
                iconNameClose="arrow_drop_up"
                iconNameOpen="arrow_drop_down"
                nativeOnMobile={false}
                panelHeight={panelHeight}
                panelWidth="100%"
                searchable
                defaultText="Select..."
                onChange={e => {
                    setSelectedValue(e.value);
                }}
                buttonSize="medium"
                virtualScroll={false}
                value={selectedValue}
                error={hasError ? `${sanitize(label)} is required.` : undefined}
            >
                {dropdownItems.map((item, index) => (
                    <DropdownItem key={`${item.value}${index}`} itemText={item.itemText} value={item.value} />
                ))}
            </Dropdown>
        </div>
    );
}

export default DropdownField;